import React from 'react';
import { Box, Button, Text, Badge, Flex } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faCircleCheck,
  faCircleXmark,
  faShieldXmark,
} from '@fortawesome/pro-solid-svg-icons';
import type { SenderTrustDecision } from '../../../../../api/cmail/cmail.dto';
import {
  formatFlagName,
  isNegativeFlag,
} from '../../../utils/senderTrustUtils';
import classes from './ApproveSenderModal.module.css';

interface SecurityFlagItemProps {
  flagKey: string;
  flagValue: boolean;
  icon: React.ReactElement<any>;
  iconColor: string;
}

const SecurityFlagItem = ({
  flagKey,
  flagValue,
  icon,
  iconColor,
}: SecurityFlagItemProps) => (
  <Flex align="center" mb={4}>
    {React.cloneElement(icon, {
      style: {
        color: iconColor,
        fontSize: '1rem',
        marginRight: '0.5rem',
      },
    })}
    <Text size="sm">{formatFlagName(flagKey, flagValue)}</Text>
  </Flex>
);

interface ChecksSectionProps {
  flags: Array<[string, boolean]>;
}

const FailedChecksSection = ({ flags }: ChecksSectionProps) => {
  if (flags.length === 0) return null;

  return (
    <Box mb="sm">
      <Text size="sm" c="red" fw={600} mb="xs">
        Failed Checks
      </Text>
      <Box className={classes.failedBox}>
        {flags.map(([key, value]) => (
          <SecurityFlagItem
            key={key}
            flagKey={key}
            flagValue={value}
            icon={<FontAwesomeIcon icon={faCircleXmark} />}
            iconColor="var(--mantine-color-red-6)"
          />
        ))}
      </Box>
    </Box>
  );
};

const PassedChecksSection = ({ flags }: ChecksSectionProps) => {
  if (flags.length === 0) return null;

  return (
    <Box>
      <Text size="sm" c="green" fw={600} mb="xs">
        Passed Checks
      </Text>
      <Box className={classes.passedBox}>
        {flags.map(([key, value]) => (
          <SecurityFlagItem
            key={key}
            flagKey={key}
            flagValue={value}
            icon={<FontAwesomeIcon icon={faCircleCheck} />}
            iconColor="var(--mantine-color-green-6)"
          />
        ))}
      </Box>
    </Box>
  );
};

interface ApproveSenderModalData {
  senderTrustDecision: SenderTrustDecision;
  senderEmail: string;
  senderName: string;
  onApprove: () => Promise<void>;
}

interface ApproveSenderModalContentProps {
  data: ApproveSenderModalData;
  close: () => void;
}

const ApproveSenderModalContent = ({
  data,
  close,
}: ApproveSenderModalContentProps) => {
  const [isApproving, setIsApproving] = React.useState(false);

  const { senderTrustDecision, senderEmail, senderName, onApprove } = data;

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await onApprove();
      close();
    } catch (error) {
      setIsApproving(false);
    }
  };

  const passedFlags = Object.entries(senderTrustDecision.flags).filter(
    ([key, value]) => {
      return isNegativeFlag(key) ? !value : value;
    }
  );

  const failedFlags = Object.entries(senderTrustDecision.flags).filter(
    ([key, value]) => {
      return isNegativeFlag(key) ? value : !value;
    }
  );

  return (
    <>
      <Box className={classes.contentScroll}>
        <Box mb="md">
          <Text size="sm" fw={600} mb="xs">
            Sender Information
          </Text>
          <Box className={classes.infoBox}>
            <Text size="sm">
              <strong>Name:</strong> {senderName}
            </Text>
            <Text size="sm">
              <strong>Email:</strong> {senderEmail}
            </Text>
            {senderTrustDecision.alignedDomain && (
              <Text size="sm">
                <strong>Aligned Domain:</strong>{' '}
                {senderTrustDecision.alignedDomain}
              </Text>
            )}
          </Box>
        </Box>

        <Box mb="md">
          <Text size="sm" fw={600} mb="xs">
            Trust Level
          </Text>
          <Badge color="red" fw={600}>
            {senderTrustDecision.trustLevel}
          </Badge>
        </Box>

        {senderTrustDecision.reasons.length > 0 && (
          <Box mb="md">
            <Text size="sm" fw={600} mb="xs">
              Security Assessment Reasons
            </Text>
            <Box className={classes.infoBox}>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: '1.5rem',
                  listStyleType: 'disc',
                }}
              >
                {senderTrustDecision.reasons.map((reason, index) => (
                  <li key={index}>
                    <Text size="sm">{reason}</Text>
                  </li>
                ))}
              </ul>
            </Box>
          </Box>
        )}

        <Box mb="sm">
          <Text size="sm" fw={600} mb="xs">
            Security Checks
          </Text>
          <FailedChecksSection flags={failedFlags} />
          <PassedChecksSection flags={passedFlags} />
        </Box>

        <Box className={classes.warningBox}>
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            style={{
              color: 'var(--mantine-color-yellow-6)',
              marginRight: '0.5rem',
              marginTop: '0.125rem',
            }}
          />
          <Text size="sm" c="dimmed">
            This message has been quarantined due to low trust score. Approving
            this sender will allow the system to process this message and future
            messages from this sender.
          </Text>
        </Box>
      </Box>

      <Box className={classes.footer}>
        <Flex justify="flex-end" gap="sm">
          <Button
            onClick={close}
            variant="outline"
            color="blue"
            disabled={isApproving}
            fw={600}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="filled"
            color="blue"
            disabled={isApproving}
            fw={600}
          >
            {isApproving ? 'Approving...' : 'Approve Sender'}
          </Button>
        </Flex>
      </Box>
    </>
  );
};

export const ApproveSenderModalConfig = {
  ApproveSenderModal: {
    component: ApproveSenderModalContent,
    config: {
      maxWidth: 'md',
      fullWidth: true,
      title: 'Approve Sender',
      icon: <FontAwesomeIcon icon={faShieldXmark} />,
    },
  },
};
